import React, { useState, useEffect } from "react";
import { Col, Container, Row } from "reactstrap";
import s from "./styles.scss";
import withStyles from "isomorphic-style-loader/withStyles";
import PrimaryButton from "../../../toolkit/primaryButton";
import AuthLeftPanel from "../AuthLeftPanel";
import LoadingSpinner from "../../../toolkit/loadingSpinner";
import PageTransition from "../../../toolkit/pageTransition";
import { withRouter, RouterProps } from "../../../utils/withRouter";
import { executeVerifyEmail } from "../../../utils/fetch";
import { serviceCaller, ServiceCallerProps } from "../../../Admin/composition";
import { MobileLogo, AuthContentCol, AuthWideFormCol } from "../shared";

interface Props extends RouterProps, ServiceCallerProps {}

enum VerificationState {
  LOADING = 'loading',
  SUCCESS = 'success',
  ERROR = 'error'
}

const VerifyEmailPage: React.FC<Props> = ({ navigate, location, config }) => {
  const [verificationState, setVerificationState] = useState<VerificationState>(VerificationState.LOADING);
  const [message, setMessage] = useState("");
  const [charityId, setCharityId] = useState("");
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [configTimeout, setConfigTimeout] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('verificationId');

    if (!id) {
      setVerificationState(VerificationState.ERROR);
      setMessage("Invalid verification link. Please check your email for the correct link.");
      return;
    }

    setVerificationId(id);
  }, [location.search]);

  useEffect(() => {
    if (!verificationId) return;

    if (config?.authServiceUrl) {
      verifyEmail(verificationId);
    } else if (verificationId && !config) {
      setVerificationState(VerificationState.LOADING);
    }
  }, [verificationId, config]);

  useEffect(() => {
    if (verificationId && !config) {
      const timer = setTimeout(() => {
        if (!config) {
          setConfigTimeout(true);
          setVerificationState(VerificationState.ERROR);
          setMessage("Unable to connect to the verification service. Please try refreshing the page or contact support if the issue persists.");
        }
      }, 10000);

      return () => clearTimeout(timer);
    }
    return () => {};
  }, [verificationId, config]);

  const verifyEmail = async (verificationId: string) => {
    try {
      if (!config?.authServiceUrl) {
        throw new Error("Configuration not available");
      }
      const response = await executeVerifyEmail(config.authServiceUrl, verificationId);

      if (response.success) {
        setVerificationState(VerificationState.SUCCESS);
        setMessage("Email verified successfully! Your charity has been created.");
        setCharityId(response.charityId || "");

        setCountdown(3);

        const countdownInterval = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(countdownInterval);
              navigate("/login", { state: { emailVerified: true } });
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setVerificationState(VerificationState.ERROR);
        setMessage(response.message || "Verification failed. Please try again.");
      }
    } catch (error: any) {
      setVerificationState(VerificationState.ERROR);
      setMessage(error.message || "Verification failed. The link may be expired or invalid. Please try signing up again.");
    }
  };

  const handleContinueToLogin = () => {
    navigate("/login", { state: { emailVerified: true } });
  };

  const handleBackToSignup = () => {
    navigate("/signup");
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const renderContent = () => {
    switch (verificationState) {
      case VerificationState.LOADING:
        return (
          <LoadingSpinner
            title="Verifying your email..."
            message="Please wait while we verify your account."
          />
        );

      case VerificationState.SUCCESS:
        return (
          <div className={s.successState}>
            <div className={s.successIcon}>
              <i className="fa-solid fa-check-circle"></i>
            </div>
            <h3 className={s.successTitle}>Email Verified!</h3>
            <p className={s.successText}>
              {message}
            </p>
          </div>
        );

      case VerificationState.ERROR:
        return (
          <div className={s.errorState}>
            <div className={s.errorIcon}>
              <i className="fa-solid fa-exclamation-triangle"></i>
            </div>
            <h3 className={s.errorTitle}>Verification Failed</h3>
            <p className={s.errorText}>
              {message}
            </p>
            <div className={s.buttonGroup}>
              {configTimeout ? (
                <PrimaryButton
                  onClick={handleRefresh}
                  className={`${s.primaryButton} w-100`}
                >
                  Refresh Page
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  onClick={handleBackToSignup}
                  className={`${s.primaryButton} w-100`}
                >
                  Back to Sign Up
                </PrimaryButton>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Container fluid className="fullscreenFixed">
      <Row className="h-100">
        <AuthLeftPanel
          title="Email Verification"
          subtitle="Confirming your account details"
          imageAlt="Verification"
        />
        <AuthContentCol className={s.rightPanel}>
          <PageTransition>
            <MobileLogo />
            <Row className="justify-content-center" style={{ marginTop: "20%" }}>
              <AuthWideFormCol className={`text-lg-start`}>
                {renderContent()}
              </AuthWideFormCol>
            </Row>
          </PageTransition>
        </AuthContentCol>
      </Row>
    </Container>
  );
};

export default withRouter(serviceCaller(withStyles(s)(VerifyEmailPage)));
